import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { County } from '../shared/api';
import { COUNTY_LABELS, COUNTY_OPTIONS } from '../shared/api';
import { config } from '../shared/config';

const STATES =
  'AL AK AZ AR CA CO CT DE DC GA HI ID IL IN IA KS KY LA ME MD MA MI MN MS MO MT NE NV NH NJ NM NY NC ND OH OK OR PA RI SC SD TN TX UT VT VA WA WV WI WY PR'.split(' ');

const FIELD = 'mt-1 w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-ink';

/**
 * Public early-access request. Florida contractors get a demo and an account
 * once Marcus approves; other states join a waitlist (Florida law only today).
 * Linked from posts as /early-access?src=<where-the-link-was>.
 */
export default function EarlyAccess() {
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    state: 'FL',
    county: '' as County | 'other' | '',
    role: '',
    jobsPerYear: '',
    note: '',
    website: '',
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<{ florida: boolean } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const set = (k: keyof typeof form, v: string) => setForm((f) => ({ ...f, [k]: v }));
  const florida = form.state === 'FL';

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`${config.apiUrl}/early-access`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          county: !florida || !form.county ? null : form.county === 'other' ? 'Other Florida county' : COUNTY_LABELS[form.county],
          role: form.role,
          jobsPerYear: form.jobsPerYear || null,
          source: params.get('src'),
        }),
      });
      const body = (await res.json().catch(() => ({}))) as { florida?: boolean; error?: string };
      if (!res.ok) throw new Error(body.error ?? 'That did not go through. Check the fields and try again.');
      setDone({ florida: body.florida !== false });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That did not go through.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-start justify-center bg-bg px-4 py-12">
      <div className="w-full max-w-lg space-y-6">
        <div>
          <p className="text-2xl font-extrabold tracking-tight text-ink">
            Lien<span className="text-tint">Guardian</span>
          </p>
          <h1 className="mt-4 text-xl font-bold text-ink">Early access for Florida contractors</h1>
          <p className="mt-1 text-sm text-muted">
            LienGuardian tracks your Florida lien-law deadlines on every job and prepares the Notice to Owner, waivers and the
            rest from the property record. Free during early access. Tell us about your company and we will set up a short demo.
          </p>
        </div>

        {done ? (
          <div className="rounded-lg border border-ok/40 bg-ok/10 p-4 text-sm text-ink">
            {done.florida
              ? 'Request received. Check your email for a confirmation; Marcus will be in touch to set up your demo and account.'
              : 'You are on the waitlist. LienGuardian covers Florida lien law today; we will email you if that changes for your state.'}
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3 rounded-lg border border-border bg-surface p-4">
            <label className="block text-sm font-semibold text-ink">
              Your name
              <input required minLength={2} value={form.name} onChange={(e) => set('name', e.target.value)} className={FIELD} />
            </label>
            <label className="block text-sm font-semibold text-ink">
              Email
              <input required type="email" value={form.email} onChange={(e) => set('email', e.target.value)} className={FIELD} />
            </label>
            <label className="block text-sm font-semibold text-ink">
              {form.role === 'homeowner' ? 'Company (optional)' : 'Company'}
              <input required={form.role !== 'homeowner'} minLength={2} autoComplete="organization" value={form.company} onChange={(e) => set('company', e.target.value)} className={FIELD} />
            </label>
            <label className="block text-sm font-semibold text-ink">
              Phone
              <input
                required
                type="tel"
                autoComplete="tel"
                pattern="(?:\D*\d){10,}\D*"
                title="Phone number with area code"
                value={form.phone}
                onChange={(e) => set('phone', e.target.value)}
                className={FIELD}
              />
            </label>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <label className="block text-sm font-semibold text-ink">
                State you work in
                <select value={form.state} onChange={(e) => set('state', e.target.value)} className={FIELD}>
                  <option value="FL">Florida</option>
                  {STATES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </label>
              {florida && (
                <label className="block text-sm font-semibold text-ink">
                  Main county
                  <select required value={form.county} onChange={(e) => set('county', e.target.value)} className={FIELD}>
                    <option value="">Choose…</option>
                    {COUNTY_OPTIONS.map((c) => (
                      <option key={c} value={c}>
                        {COUNTY_LABELS[c]}
                      </option>
                    ))}
                    <option value="other">My county is not listed</option>
                  </select>
                </label>
              )}
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <label className="block text-sm font-semibold text-ink">
                You usually work as
                <select required value={form.role} onChange={(e) => set('role', e.target.value)} className={FIELD}>
                  <option value="">Choose…</option>
                  <option value="prime">Prime / general contractor</option>
                  <option value="sub">Subcontractor</option>
                  <option value="supplier">Supplier</option>
                  <option value="homeowner">Homeowner (watching my own property)</option>
                  <option value="other">Other</option>
                </select>
              </label>
              <label className="block text-sm font-semibold text-ink">
                Jobs a year
                <select value={form.jobsPerYear} onChange={(e) => set('jobsPerYear', e.target.value)} className={FIELD}>
                  <option value="">Choose…</option>
                  <option value="1-5">1–5</option>
                  <option value="6-20">6–20</option>
                  <option value="21-50">21–50</option>
                  <option value="50+">50+</option>
                </select>
              </label>
            </div>
            <label className="block text-sm font-semibold text-ink">
              Anything we should know? (optional)
              <textarea value={form.note} onChange={(e) => set('note', e.target.value)} className={`${FIELD} min-h-[4rem]`} />
            </label>
            {/* Honeypot: hidden from people, filled by bots. */}
            <input
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              value={form.website}
              onChange={(e) => set('website', e.target.value)}
              className="hidden"
              name="website"
            />
            {!florida && (
              <p className="text-xs text-muted">LienGuardian covers Florida lien law today. Outside Florida, this adds you to the waitlist.</p>
            )}
            {error && <p className="text-sm text-urgent">{error}</p>}
            <button type="submit" disabled={busy} className="rounded-lg bg-tint px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
              {busy ? 'Sending…' : florida ? 'Request early access' : 'Join the waitlist'}
            </button>
          </form>
        )}
        <p className="text-xs text-dim">
          LienGuardian tracks deadlines and prepares documents from what you enter. It is not a law firm and does not give legal
          advice.
        </p>
      </div>
    </div>
  );
}
